// src/components/ambassador/ProspectNew.jsx
//
// /ambassador/new - add an establishment the map doesn't have, standing in
// front of it: the phone's position is its position. The server checks for
// duplicates (SIRET, phone, address + name, name within 30 m); the new
// prospect can be reserved at once and an admin reviews it.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Paper, Stack, Button, TextField, MenuItem, Alert } from '@mui/material';
import { useAmbassador } from './AmbassadorApp';
import { ambassadorPost } from '../../utils/ambassadorApi';
import { FACILITY_TYPES, errorText } from '../../utils/ambassadorStatus';

const EMPTY = { name: '', facilityType: 'restaurant', address: '', postalCode: '', city: 'Paris', phone: '', siret: '' };

export default function ProspectNew() {
  const { getToken } = useAmbassador();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [position, setPosition] = useState(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const locate = () => {
    if (!navigator.geolocation) { setError('Localisation indisponible sur ce téléphone.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setPosition({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }); setLocating(false); },
      () => { setError('Position refusée : autorisez la localisation pour ajouter un établissement.'); setLocating(false); },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 });
  };
  useEffect(() => { locate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    setBusy(true); setError('');
    try {
      const body = Object.fromEntries(Object.entries({ ...form, lat: position.lat, lon: position.lon })
        .filter(([, v]) => v !== ''));
      setCreated(await ambassadorPost('/prospects', getToken(), body));
    } catch (e) {
      if (e.message === 'PROSPECT_EXISTS' && e.details?.id) navigate(`/ambassador/prospects/${e.details.id}`);
      else setError(errorText(e));
    } finally { setBusy(false); }
  };

  if (created) {
    return (
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Alert severity={created.duplicateOf.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
          {created.duplicateOf.length
            ? 'Établissement ajouté, mais il ressemble à une fiche existante : un administrateur va vérifier.'
            : 'Établissement ajouté. Un administrateur va vérifier la fiche.'}
        </Alert>
        <Stack spacing={1}>
          <Button variant="contained" onClick={() => navigate(`/ambassador/prospects/${created.id}`)}>Ouvrir la fiche</Button>
          {created.duplicateOf.map((id) => (
            <Button key={id} variant="outlined" onClick={() => navigate(`/ambassador/prospects/${id}`)}>Voir la fiche qui ressemble (#{id})</Button>
          ))}
          <Button onClick={() => { setCreated(null); setForm(EMPTY); }}>Ajouter un autre établissement</Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Ajouter un établissement</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Il n’est pas sur la carte ? Ajoutez-le depuis le trottoir : sa position est celle de votre téléphone.
      </Typography>
      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Alert severity={position ? 'success' : 'info'} action={<Button size="small" onClick={locate} disabled={locating}>{position ? 'Relocaliser' : 'Me localiser'}</Button>}>
            {locating ? 'Localisation…' : position ? `Position trouvée (± ${Math.round(position.accuracy)} m)` : 'Position nécessaire'}
          </Alert>
          <TextField label="Nom (enseigne)" required value={form.name} onChange={set('name')} />
          <TextField select label="Type" value={form.facilityType} onChange={set('facilityType')}>
            {FACILITY_TYPES.map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
          </TextField>
          <TextField label="Adresse" placeholder="12 rue Oberkampf" value={form.address} onChange={set('address')} />
          <Stack direction="row" spacing={1}>
            <TextField label="Code postal" value={form.postalCode} onChange={set('postalCode')} inputProps={{ inputMode: 'numeric', maxLength: 5 }} sx={{ width: 140 }} />
            <TextField label="Ville" value={form.city} onChange={set('city')} sx={{ flex: 1 }} />
          </Stack>
          <TextField label="Téléphone" type="tel" value={form.phone} onChange={set('phone')} />
          <TextField label="SIRET (facultatif)" value={form.siret} onChange={set('siret')} inputProps={{ inputMode: 'numeric' }} />
          <Button variant="contained" size="large" disabled={busy || !position || !form.name.trim()} onClick={submit}>
            Ajouter
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
